import type {
  ActionItem,
  Assignee,
  Patron,
  PatronRef,
  Meeting,
  MeetingDetail,
  Participant,
  Summary,
  TranscriptSegment,
} from '@/types'
import { people } from './people'

const hoursAgo = (h: number) => new Date(Date.now() - h * 36e5).toISOString()
const hoursAhead = (h: number) => new Date(Date.now() + h * 36e5).toISOString()

/**
 * Two patrons, one of them attached BY INVITE and one BY HAND, because the UI
 * has to render those two differently — a derived chip has no remove button.
 */
export const patrons: Patron[] = [
  {
    id: 'cl1', name: 'Northwind', status: 'active', notes: null,
    domains: ['northwind.example'], meetingCount: 2, openActionCount: 3,
    createdAt: new Date(Date.now() - 30 * 864e5).toISOString(),
  },
  {
    id: 'cl2', name: 'FMS Labs', status: 'active', notes: null,
    domains: ['fmslabs.example'], meetingCount: 1, openActionCount: 1,
    createdAt: new Date(Date.now() - 12 * 864e5).toISOString(),
  },
  {
    id: 'cl3', name: 'Kestrel', status: 'archived', notes: null,
    domains: [], meetingCount: 0, openActionCount: 0,
    createdAt: new Date(Date.now() - 200 * 864e5).toISOString(),
  },
]

const northwindByInvite: PatronRef[] = [
  { id: 'cl1', name: 'Northwind', status: 'active', source: 'invite' },
]
const northwindByHand: PatronRef[] = [
  { id: 'cl1', name: 'Northwind', status: 'active', source: 'manual' },
]

const base = {
  tenantId: 't1',
  isShared: false,
  patrons: [] as PatronRef[],
  tags: [] as string[],
  seriesId: null as string | null,
  isRecurring: false,
}

export const meetings: Meeting[] = [
  {
    ...base,
    id: 'c2fdbd6273406bb9-20260828T1030',
    seriesId: 'sr-fms-weekly',
    isRecurring: true,
    title: 'FMS rollout — weekly sync',
    // Internal: nobody from Northwind was on it, so no derivation can see it.
    // Somebody tagged it, which is the only signal this case can ever have.
    patrons: northwindByHand,
    platform: 'teams',
    status: 'ready',
    startsAt: hoursAgo(3),
    endsAt: hoursAgo(2.2),
    durationSeconds: 2_760,
    organizer: people[0],
    participants: [people[0], people[1], people[2], people[3]],
    languages: ['en-IN', 'hi-IN'],
    leaveReason: 'ended_normally',
    clockCheck: { verdict: 'ok', originsAgree: true, namedFraction: 0.92, longestSegmentSeconds: 74 },
    cleanTranscript: { generatedAt: '2026-08-28T11:04:00Z', provider: 'bedrock', model: 'deepseek.v3.2', edits: 1 },
    hasRecording: true,
    actionItemCount: 4,
    decisionCount: 3,
    tags: ['engineering', 'weekly'],
  },
  {
    ...base,
    id: 'ef692f352b05aad7-20260828T0700',
    seriesId: 'sr-fms-weekly',
    isRecurring: true,
    title: 'Northwind — discovery call',
    // Derived: Neha is at northwind.example, which the patron claims.
    patrons: northwindByInvite,
    platform: 'meet',
    status: 'ready',
    startsAt: hoursAgo(7),
    endsAt: hoursAgo(6.4),
    durationSeconds: 2_130,
    organizer: people[1],
    participants: [people[1], people[0], people[4]],
    languages: ['en-IN'],
    leaveReason: 'ended_normally',
    clockCheck: { verdict: 'suspect', originsAgree: false, namedFraction: 0.41, longestSegmentSeconds: 122 },
    cleanTranscript: null,
    hasRecording: true,
    actionItemCount: 6,
    decisionCount: 1,
    isShared: true,
    tags: ['sales'],
  },
  {
    ...base,
    id: '8913f21c05965045-20260827T0930',
    seriesId: 'sr-fms-weekly',
    isRecurring: true,
    title: 'Design review — transcript viewer',
    platform: 'zoom',
    status: 'ready',
    startsAt: hoursAgo(26),
    endsAt: hoursAgo(25.3),
    durationSeconds: 2_460,
    organizer: people[2],
    participants: [people[2], people[0], people[3], people[5]],
    languages: ['en-IN'],
    leaveReason: 'ended_normally',
    // origins unknown rather than disagreeing — the honest third state.
    clockCheck: { verdict: 'ok', originsAgree: null, namedFraction: 0.78, longestSegmentSeconds: 61 },
    cleanTranscript: null,
    hasRecording: true,
    actionItemCount: 3,
    decisionCount: 2,
    tags: ['design'],
  },
  {
    ...base,
    id: 'a71c0e91b3d2f004-20260828T1400',
    title: 'Pipeline review',
    platform: 'teams',
    status: 'processing',
    startsAt: hoursAgo(1.2),
    endsAt: hoursAgo(0.4),
    durationSeconds: 2_880,
    organizer: people[0],
    participants: [people[0], people[1], people[4]],
    languages: ['en-IN'],
    leaveReason: 'ended_normally',
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: true,
    actionItemCount: 0,
    decisionCount: 0,
    tags: ['sales'],
  },
  {
    ...base,
    id: 'b0447f1a2e6c9911-20260828T1600',
    title: 'Ops standup',
    platform: 'meet',
    status: 'recording',
    startsAt: hoursAgo(0.2),
    endsAt: hoursAhead(0.3),
    durationSeconds: null,
    organizer: people[3],
    participants: [people[3], people[2]],
    languages: ['en-IN', 'hi-IN'],
    leaveReason: null,
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: true,
    actionItemCount: 0,
    decisionCount: 0,
  },
  {
    ...base,
    id: 'd51e8a7c4409bb31-20260828T1800',
    title: 'Board pre-read walkthrough',
    platform: 'zoom',
    status: 'scheduled',
    startsAt: hoursAhead(2),
    endsAt: hoursAhead(3),
    durationSeconds: null,
    organizer: people[0],
    participants: [people[0], people[1], people[4], people[5]],
    languages: ['en-IN'],
    leaveReason: null,
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: false,
    actionItemCount: 0,
    decisionCount: 0,
    tags: ['leadership'],
  },
  {
    ...base,
    id: 'e6620cc19a4d7f8a-20260829T0930',
    title: 'Hiring loop — backend',
    platform: 'teams',
    status: 'scheduled',
    startsAt: hoursAhead(19),
    endsAt: hoursAhead(20),
    durationSeconds: null,
    organizer: people[1],
    participants: [people[1], people[3]],
    languages: ['en-IN'],
    leaveReason: null,
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: false,
    actionItemCount: 0,
    decisionCount: 0,
  },
  {
    ...base,
    id: 'f0912ab4c7e63d55-20260827T1130',
    title: 'Vendor call — transcription pricing',
    platform: 'meet',
    status: 'failed',
    startsAt: hoursAgo(29),
    endsAt: hoursAgo(28.5),
    durationSeconds: null,
    organizer: people[0],
    participants: [people[0], people[5]],
    languages: ['en-IN'],
    leaveReason: 'not_admitted',
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: false,
    actionItemCount: 0,
    decisionCount: 0,
  },
  // ONE MEETING PER FAILURE BUCKET, so each of the five Status options is
  // demonstrably non-empty against the mock. With only the `not_admitted`
  // row above, the rest came back empty in dev -- which reads as a broken
  // filter rather than as a quiet week, and is the state in which nobody
  // notices the filter is not wired up at all.
  {
    ...base,
    id: 'd4e5f6071829303a-20260827T1000',
    title: 'Partner sync — cancelled before admission',
    platform: 'teams',
    status: 'failed',
    startsAt: hoursAgo(30),
    endsAt: hoursAgo(29.5),
    durationSeconds: null,
    organizer: people[0],
    participants: [people[0], people[4]],
    languages: ['en-IN'],
    leaveReason: 'meeting_cancelled',
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: false,
    actionItemCount: 0,
    decisionCount: 0,
  },
  {
    ...base,
    id: 'a1b2c3d4e5f60718-20260827T0900',
    title: 'Board prep — Q3 numbers',
    platform: 'teams',
    status: 'failed',
    startsAt: hoursAgo(31),
    endsAt: hoursAgo(30.5),
    durationSeconds: null,
    organizer: people[0],
    participants: [people[0], people[1]],
    languages: ['en-IN'],
    leaveReason: 'denied',
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: false,
    actionItemCount: 0,
    decisionCount: 0,
  },
  {
    ...base,
    id: 'b2c3d4e5f6071829-20260827T0800',
    title: 'Design sync — onboarding flow',
    platform: 'meet',
    status: 'failed',
    startsAt: hoursAgo(32),
    endsAt: hoursAgo(31.5),
    durationSeconds: null,
    organizer: people[0],
    participants: [people[0], people[2]],
    languages: ['en-IN'],
    leaveReason: 'join_failed',
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: false,
    actionItemCount: 0,
    decisionCount: 0,
  },
  {
    // The plain "Failed" bucket's own member: `error` is the only failure
    // reason left in it once the three host/platform outcomes move out.
    ...base,
    id: 'c3d4e5f607182930-20260827T0700',
    title: 'Support escalation review',
    platform: 'teams',
    status: 'failed',
    startsAt: hoursAgo(33),
    endsAt: hoursAgo(32.5),
    durationSeconds: 1800,
    organizer: people[0],
    participants: [people[0], people[3]],
    languages: ['en-IN'],
    leaveReason: 'error',
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: true,
    actionItemCount: 0,
    decisionCount: 0,
  },
  {
    ...base,
    id: '11aa22bb33cc44dd-20260826T1500',
    title: '1:1 — Khushi',
    platform: 'teams',
    status: 'skipped',
    startsAt: hoursAgo(50),
    endsAt: hoursAgo(49.5),
    durationSeconds: null,
    organizer: people[0],
    participants: [people[0], people[2]],
    languages: ['en-IN'],
    leaveReason: null,
    clockCheck: null,
    cleanTranscript: null,
    hasRecording: false,
    actionItemCount: 0,
    decisionCount: 0,
    tags: ['1:1'],
  },
]

const summaryFor: Summary = {
  headline:
    'FMS cutover stays on 5 September; reconciliation moves to hourly first and Varun owns the ' +
    'rollback call.',
  narrative:
    'The team reviewed the FMS rollout ahead of the 5 September cutover. Varun opened with the ' +
    'migration status: 14 of 18 sites are on the new schema and the remaining four are blocked on ' +
    'a vendor export. Kareena raised that the reconciliation job is still running twice a day, ' +
    'which leaves a six-hour window where the two systems disagree, and the group agreed to move ' +
    'it to hourly before cutover rather than after. Khushi walked through the support runbook and ' +
    'flagged that no one owns the rollback decision on the night of the cutover; Varun took that. ' +
    'The call closed on staffing for the cutover weekend, which is agreed in principle and needs ' +
    'confirmation from Shivam once the on-call roster is published.',
  keyPoints: [
    { id: 'k1', text: '14 of 18 sites migrated; four blocked on a vendor export due 2 September.' },
    { id: 'k2', text: 'Reconciliation runs twice daily, leaving a six-hour divergence window.' },
    { id: 'k3', text: 'Support runbook is written but the rollback owner was unassigned until this call.' },
    { id: 'k4', text: 'Cutover weekend staffing agreed in principle, pending the on-call roster.' },
  ],
  // False: this fixture's list is the summariser's own. `derivedSummaryFor`
  // below carries the other state, and `meetingDetails` gives it to one
  // meeting in the recurring series -- so the card's two renderings AND the
  // series view's mixed case are all reachable in mock mode. Neither state
  // should be visible only against a real database.
  keyPointsDerived: false,
  decisions: [
    {
      id: 'd1',
      text: 'Move the reconciliation job to hourly before the cutover, not after.',
      decidedBy: 'Varun Pandey',
      quote: 'हम इसे cutover से पहले hourly कर देंगे, बाद में नहीं.',
      atSeconds: 612,
    },
    {
      id: 'd2',
      text: 'Varun owns the rollback go/no-go decision on cutover night.',
      decidedBy: 'Varun Pandey',
      quote: 'I will make the call on the night, nobody else has the context.',
      atSeconds: 1_488,
    },
    {
      id: 'd3',
      text: 'The four blocked sites go in a second wave rather than delaying the cutover.',
      decidedBy: 'Kareena Shah',
      quote: 'Second wave. We are not holding fourteen sites for four.',
      atSeconds: 2_004,
    },
  ],
  nextSteps: [
    { id: 'n1', text: 'Reconciliation cadence change lands by 2 September.' },
    { id: 'n2', text: 'On-call roster published, then cutover staffing confirmed.' },
  ],
  openQuestions: [
    'Will the vendor export actually land on 2 September, given they have slipped once?',
    'Who covers the Sunday night window if the on-call roster puts one person on both days?',
  ],
  topics: ['FMS migration', 'Reconciliation', 'Cutover plan', 'Support runbook', 'Staffing'],
  promptVersion: 'summary/2026-08-21',
  generatedAt: hoursAgo(2),
  devanagariFieldsRemaining: 0,
}

const seg = (
  id: number,
  start: number,
  end: number,
  speaker: string | null,
  label: string,
  text: string,
  language: TranscriptSegment['language'] = 'en-IN',
  confidence = 0.93,
  // Null on all but a handful, which is the realistic shape: repair corrected
  // 22 words in 2,941 on the one real meeting it has been measured against.
  textClean: string | null = null,
): TranscriptSegment => ({
  id: `s${id}`,
  startSeconds: start,
  endSeconds: end,
  speakerName: speaker,
  speakerLabel: label,
  text,
  textClean,
  language,
  confidence,
})

const segments: TranscriptSegment[] = [
  seg(1, 3, 14, 'Varun Pandey', 'spk_0', "Alright, let's start with where the migration actually is, not where the plan says it is."),
  seg(2, 14, 31, 'Kareena Shah', 'spk_1', 'Fourteen sites are on the new schema as of last night. The four that are left are all waiting on the vendor export.'),
  seg(3, 31, 44, 'Varun Pandey', 'spk_0', 'And the vendor has committed to what date?'),
  seg(4, 44, 66, 'Kareena Shah', 'spk_1', 'Second of September. They have slipped once already, so I would not treat that as firm.'),
  seg(5, 66, 92, null, 'spk_3', 'Sorry, I joined late — is this the reconciliation thread or the schema thread?', 'en-IN', 0.52),
  // A mangled name and a mangled domain term: the two categories the repair
  // actually corrects on real Hinglish audio.
  seg(6, 92, 128, 'Khushi Verma', 'spk_2', 'Schema. Reconciliation is next. The runbook is written but the rollback owner is still blank.'),
  seg(7, 128, 171, 'Varun Pandey', 'spk_0', 'ठीक है, मैं वो ले लेता हूँ. Rollback call on the night is mine.', 'hi-IN', 0.88),
  seg(8, 171, 214, 'Shivam Rao', 'spk_4', 'On staffing — I can confirm once the on-call roster is out, which should be Friday.'),
  seg(9, 214, 268, 'Kareena Shah', 'spk_1', 'Second wave for the four blocked sites. We are not holding fourteen sites for four.'),
  seg(10, 268, 301, 'Varun Pandey', 'spk_0', 'Agreed. Khushi, can you get the reconciliation cadence change in before the second?'),
  seg(11, 301, 330, 'Khushi Verma', 'spk_2', 'Yes, it is a config change plus a smoke test. Two days at most.'),
  seg(12, 330, 358, 'Varun Pandey', 'spk_0', 'Sarina, push the doctor image to the deposit before you sign off.', 'en-IN', 0.31,
      'Kareena, push the docker image to the repository before you sign off.'),
]

/**
 * The participant-shaped `assignee` for a fixture whose owner was in the room.
 * Derived rather than typed out twice: the two fields disagreeing is precisely
 * the bug the shared serialiser exists to prevent, and a fixture that disagreed
 * would hide it.
 */
const attendedAssignee = (p: Participant): Assignee => ({
  kind: 'participant',
  id: p.id,
  displayName: p.displayName,
  email: p.email,
  avatarUrl: p.avatarUrl,
  attendedMeeting: true,
})

export const actionItems: ActionItem[] = [
  {
    id: 'a1',
    meetingId: meetings[0].id,
    meetingTitle: meetings[0].title,
    text: 'Move the reconciliation job from twice-daily to hourly and smoke-test it',
    owner: people[2],
    assignee: attendedAssignee(people[2]),
    dueDate: new Date(Date.now() + 4 * 864e5).toISOString(),
    status: 'in_progress',
    atSeconds: 301,
    ownerInferred: false,
    origin: 'summary',
    patrons: [],
    patronOverride: false,
    pushedTo: [{ provider: 'Jira', url: 'https://example.atlassian.net/browse/FMS-412' }],
  },
  {
    id: 'a2',
    meetingId: meetings[0].id,
    meetingTitle: meetings[0].title,
    text: 'Confirm cutover weekend staffing once the on-call roster is published',
    owner: people[3],
    assignee: attendedAssignee(people[3]),
    dueDate: new Date(Date.now() + 2 * 864e5).toISOString(),
    status: 'open',
    atSeconds: 214,
    ownerInferred: false,
    origin: 'summary',
    patrons: [],
    patronOverride: false,
    pushedTo: [],
  },
  {
    id: 'a3',
    meetingId: meetings[0].id,
    meetingTitle: meetings[0].title,
    text: 'Chase the vendor export commitment for the four blocked sites',
    owner: people[1],
    assignee: attendedAssignee(people[1]),
    dueDate: new Date(Date.now() + 864e5).toISOString(),
    status: 'open',
    atSeconds: 44,
    ownerInferred: true,
    origin: 'summary',
    patrons: [],
    patronOverride: false,
    pushedTo: [],
  },
  {
    id: 'a4',
    meetingId: meetings[0].id,
    meetingTitle: meetings[0].title,
    text: 'Publish the rollback decision tree in the support runbook',
    owner: null,
    assignee: null,
    dueDate: null,
    status: 'open',
    atSeconds: 128,
    ownerInferred: false,
    origin: 'summary',
    patrons: [],
    patronOverride: false,
    pushedTo: [],
  },
  {
    id: 'a5',
    meetingId: meetings[1].id,
    meetingTitle: meetings[1].title,
    text: 'Send Northwind the security questionnaire and data-residency note',
    owner: people[1],
    assignee: attendedAssignee(people[1]),
    dueDate: new Date(Date.now() + 3 * 864e5).toISOString(),
    status: 'open',
    atSeconds: 890,
    ownerInferred: false,
    origin: 'summary',
    patrons: [],
    patronOverride: false,
    pushedTo: [],
  },
  {
    id: 'a6',
    meetingId: meetings[2].id,
    meetingTitle: meetings[2].title,
    text: 'Ship the transcript virtualisation spike and measure at 4k segments',
    owner: people[0],
    assignee: attendedAssignee(people[0]),
    dueDate: new Date(Date.now() - 864e5).toISOString(),
    status: 'done',
    atSeconds: 1_020,
    ownerInferred: false,
    origin: 'summary',
    patrons: [],
    patronOverride: false,
    pushedTo: [],
  },
  {
    // A colleague with a seat who was NOT on the invite. `owner` is null
    // because there is no roster row: this is the case that used to render as
    // "Unassigned" in the actions list and as a name on the meeting page.
    id: 'a7',
    meetingId: meetings[0].id,
    meetingTitle: meetings[0].title,
    text: 'Get security sign-off on the new reconciliation cadence',
    owner: null,
    assignee: {
      kind: 'member',
      id: 'u5',
      displayName: 'Priya Nair',
      email: 'priya.nair@pssgway.com',
      avatarUrl: null,
      attendedMeeting: false,
    },
    dueDate: new Date(Date.now() + 6 * 864e5).toISOString(),
    status: 'open',
    atSeconds: 268,
    ownerInferred: false,
    origin: 'summary',
    patrons: [],
    patronOverride: false,
    pushedTo: [],
  },
  {
    // Somebody with no account here at all, added by hand after the call. No
    // timecode, no access granted, nothing emailed to them.
    id: 'a8',
    meetingId: meetings[0].id,
    meetingTitle: meetings[0].title,
    text: 'Ask outside counsel whether the residency note needs re-signing',
    owner: null,
    assignee: {
      kind: 'external',
      id: null,
      displayName: 'Outside Counsel',
      email: 'counsel@advisors.example',
      avatarUrl: null,
      attendedMeeting: false,
    },
    dueDate: null,
    status: 'open',
    atSeconds: null,
    ownerInferred: false,
    origin: 'manual',
    patrons: [],
    patronOverride: false,
    pushedTo: [],
  },
]
// THE FALLBACK RENDERING, for a summary written before the prompt asked for key
// points. Every point here is a SPAN OF THE NARRATIVE above, clipped and
// stripped and otherwise byte-identical -- which is the rule `derive_key_points`
// actually implements, and the reason a derived point is safe to show beside a
// model-produced one. A fixture that invented prettier sentences would make the
// card look better in mock mode than it can ever look in the product.
const derivedSummaryFor: Summary = {
  ...summaryFor,
  keyPoints: [
    { id: 'd-k1', text: 'The team reviewed the FMS rollout ahead of the 5 September cutover.' },
  ],
  keyPointsDerived: true,
}

// THE OLDEST OCCURRENCE CARRIES THE DERIVED SUMMARY, which is what the series
// view is for: it spans months, so it is the one screen where a summary written
// before the prompt changed sits next to one written after. That mixed case has
// its own sentence on the card, and until this fixture existed it could not be
// seen without a real database.
const derivedSummaryMeetingId = '8913f21c05965045-20260827T0930'

// A summary produced with `SUMMARY_SECTIONS=1`: an overview, the unchanged full
// narrative (which the email sends) and a section per topic. Every other fixture has NO sections -- which is every real
// summary today -- so mock mode shows both renderings side by side, and the one
// sectioned meeting carries a section with no timestamp (the em-dash case) as
// well as ones with real offsets. `summaryFor` is deliberately left as it was:
// a fixture that quietly gained sections everywhere would stop showing the
// narrative-only card that every existing meeting actually has.
const sectionedSummaryFor: Summary = {
  ...summaryFor,
  overview:
    'The FMS cutover stays on 5 September, with reconciliation moving to hourly first and ' +
    'Varun owning the rollback call.',
  sections: [
    {
      heading: 'Migration status',
      timestampSeconds: 95,
      summary:
        '14 of 18 sites are on the new schema. The remaining four are blocked on a vendor ' +
        'export due 2 September.',
    },
    {
      heading: 'Reconciliation frequency',
      timestampSeconds: 382,
      summary:
        'The job runs twice a day, leaving a six-hour window where the two systems disagree. ' +
        'The group agreed to move it to hourly before the cutover rather than after.',
    },
    {
      heading: 'Rollback ownership',
      timestampSeconds: null,
      summary:
        'No one owned the rollback decision on the night of the cutover; Varun took it.',
    },
  ],
}
const sectionedSummaryMeetingId = meetings.find(
  (m) => m.status === 'ready' && m.id !== derivedSummaryMeetingId,
)?.id

export const meetingDetails: Record<string, MeetingDetail> = Object.fromEntries(
  meetings.map((m) => [
    m.id,
    {
      ...m,
      summary:
        m.status !== 'ready'
          ? null
          : m.id === derivedSummaryMeetingId
            ? derivedSummaryFor
            : m.id === sectionedSummaryMeetingId
              ? sectionedSummaryFor
              : summaryFor,
      segments: m.status === 'ready' ? segments : [],
      actionItems: actionItems.filter((a) => a.meetingId === m.id),
      // MOCK MODE'S ANSWER TO A QUESTION ONLY THE SERVER CAN REALLY ANSWER.
      // The real rule also weighs a request already in flight and a
      // per-meeting cap, neither of which a fixture has; what it needs to show
      // is the button on a meeting the host refused, so `denied` is the case
      // it models. `not_admitted` and `join_failed` are equally retryable in
      // production and left out here deliberately -- a fixture where every
      // failed meeting carries the button would hide a `canRejoin` that had
      // stopped being read at all.
      canRejoin: m.leaveReason === 'denied',
    },
  ]),
)
